// BHP-1083: domain read-models over Backend2.0.
//
// - Domain: item/collection shape from `GET /api/domains` (DomainOutputDto).
// - DomainAvailability: one entry per TLD from `GET /api/domain-availability`.
// Since BHP-1126 the endpoint returns OUR amounts — the catalogue price with the
// customer's margin — and two of them, because registering and renewing differ
// for most TLDs. The frontend only formats and labels them; no margin math here.

/**
 * Mirrors the backend enum `App\Domain\Enum\DomainRegistrationStatus`.
 * `cancelled` is set by `DELETE /api/domains/{uuid}` (auto-renew off at the
 * registrar; the domain keeps resolving until expiry) and is NOT filtered out
 * of `GET /api/domains`, so the overview must be able to render it.
 */
export type DomainRegistrationStatus =
  'pending' | 'registering' | 'registered' | 'failed' | 'cancelled';

/** Statuses the backend can still move away from — the detail page polls these. */
const IN_FLIGHT_STATUSES: readonly DomainRegistrationStatus[] = [
  'pending',
  'registering'
];

export function isRegistrationInFlight(
  status: DomainRegistrationStatus | undefined
): boolean {
  return status !== undefined && IN_FLIGHT_STATUSES.includes(status);
}

export interface Domain {
  uuid: string;
  domainName: string;
  tld: string | null;
  isDnsOnly: boolean;
  registrationStatus: DomainRegistrationStatus;
  registrationFailureReason: string | null;
  renewalDate: string | null;
  createdOn: string | null;
  /** BHP-1063: null until the zone is provisioned after a successful registration. */
  dnsZoneUuid: string | null;
}

export interface DomainAvailability {
  /** Full domain, e.g. "acme.nl". */
  domain: string;
  available: boolean;
  premium: boolean;
  priceCurrency: string | null;
  /**
   * BHP-1126/1127: what registering costs — the first year for a TLD with a
   * promotional rate. Our catalogue price with the customer's margin, not the
   * registrar's amount.
   */
  priceAmount: number | null;
  /** What renewing costs per year, same basis. */
  renewalPriceAmount: number | null;
  /** Raw OpenProvider status ("free", "active", …). */
  status: string | null;
}

/**
 * BHP-1127: one money formatter, used by the results list and the registration
 * dialog alike — the dialog is the last thing a customer sees before ordering,
 * so it may not word the amounts differently from the list it came from.
 *
 * Null in, null out: an amount we do not have must stay absent instead of
 * turning into 0,00 or a dash, which on a money field reads as free (BHP-1103).
 */
export function formatAmount(
  currency: string | null,
  amount: number | null
): string | null {
  return amount === null
    ? null
    : `${currency ?? ''} ${amount.toFixed(2)}`.trim();
}

/**
 * BHP-1128: outcome of an availability check — the domains we could check, plus
 * the TLDs the backend refused because they are not in our catalogue. Those are
 * carried instead of dropped, so the page can say why an extension the customer
 * typed is not in the list.
 */
export interface AvailabilityCheck {
  results: DomainAvailability[];
  /** TLDs we do not offer, in the order they were requested. */
  unsupportedTlds: string[];
}

/**
 * BHP-1083: default TLDs queried by the availability check. The Backend2.0
 * endpoint requires an explicit `tlds[]`; this is the set the legacy UI showed
 * as examples. A catalogue-driven list (product_type='domain') is a follow-up.
 */
export const DEFAULT_TLDS = ['nl', 'com', 'org', 'net'] as const;

/** Second-level label of a search term: "acme.nl" → "acme", "acme" → "acme". */
export function toSecondLevelLabel(term: string): string {
  return term.trim().toLowerCase().split('.')[0] ?? '';
}

/**
 * The extension the user typed, if any: "acme.co.uk" → "co.uk", "acme" → null.
 * Everything after the first dot, so a compound extension stays whole.
 */
function typedTld(term: string): string | null {
  const [, ...rest] = term.trim().toLowerCase().split('.');
  const tld = rest.join('.');
  return tld.length > 0 ? tld : null;
}

/**
 * BHP-1122: which extensions to check for a search term — the typed one first,
 * so it heads the results, then the defaults, without repeating it.
 *
 * Before this, a typed extension was dropped by `toSecondLevelLabel` and only
 * the default set was queried, so a customer could not check anything outside
 * it. An extension the catalogue does not carry simply drops out of the
 * results: the availability hook already falls back to one request per TLD when
 * the backend rejects the batch (see `fetchDomainAvailability`).
 */
export function tldsForTerm(term: string): string[] {
  const typed = typedTld(term);

  if (typed === null) {
    return [...DEFAULT_TLDS];
  }

  return [typed, ...DEFAULT_TLDS.filter((tld) => tld !== typed)];
}

/**
 * TLD of a result relative to the searched label: ("acme.co.uk", "acme") →
 * "co.uk". Returns null when the result is not a child of `sld` (the backend
 * may normalise the name, e.g. IDN → punycode); callers must not POST an empty
 * TLD, which the backend would reject with an unhelpful 422.
 */
export function tldOf(domain: string, sld: string): string | null {
  if (!domain.startsWith(`${sld}.`)) {
    return null;
  }
  const tld = domain.slice(sld.length + 1);
  return tld.length > 0 ? tld : null;
}
